import { PageRequest } from '@/core/request/PageRequest';

import {
  WorkOrderComplementStatus,
  WorkOrderComplementStatusNumber,
  WorkOrderComplementTankLevel,
  WorkOrderComplementType,
} from '../WorkOrdersComplements/WorkOrdersComplements.types';
import { WorkOrderItemType } from '../WorkOrdersItems/WorkOrdersItems.types';
import { VehicleType } from '../../people/CustomerVehicles/CustomerVehicles.types';
import { WorkorderColumn } from '../WorkordersConfiguration/WorkordersConfiguration.types';
import { CustomerCategory } from '../../people/CustomerCategory/CustomerCategory.types';
import { PageResult } from '@/core/request/PageResult';
import { WorkOrderCalendarDateType } from './WorkOrders.enum';

export enum CustomerType {
  Private = 'domain.enums.work_order_customer_type.private',
  Insurance = 'domain.enums.work_order_customer_type.insurance',
}

export interface WorkOrderVehicle {
  id: number;
  licensePlate: string;
  chassi: string | null;
  model: string;
  modelId: number;
  brand: string;
  fuelName: string;
  yearFabrication: number;
  yearModel: number;
  color: string | null;
  colorId: number | null;
  type?: VehicleType;
  engineCapacity?: string;
  valves?: string;
  cylinders?: string;
  engineName?: string;
  engineTechnicalCode?: string;
  horsePower?: string[];
  image?: string;
  line?: string;
  airIntake?: string;
  rearBrake?: string;
  frontBrake?: string;
  brakeSystem?: string;
  steeringSystem?: string;
  tractionSystem?: string;
  injectionSystem?: string;
  transmissionType?: string;
  engineConfiguration?: string;
}

export interface WorkOrder {
  id: number;
  number: number;
  diagnosticId?: number;
  company: {
    id: number;
    name: string;
    nickName: string;
  };
  customer?: {
    id: number;
    name: string;
    withheld?: boolean;
    cpfCnpj: string | null;
    birthDate: string;
    showLgpd: boolean;
    enableLgpd: boolean;
    creditLimit: number;
    category?: CustomerCategory;
    foreignDocument: string | null;
  };
  vehicle: WorkOrderVehicle;
  complements: ComplementWorkOrder[];
  insurerId?: number;
  customerAddressId?: number;
  partnerDiscount?: boolean | null;
  employeeDiscount?: boolean | null;
  portoAssistantCode?: string;
  portoAssistantVehicleCode?: string;
  isPortoBatteryWorkOrder?: boolean;
  batteryWarrantyRegistered?: boolean;
  hasPortoVoucher?: boolean;
  boschCoreAccess?: boolean;
}

interface ComplementWorkOrder {
  id: number;
  number: number;
  complementIdentifier: string;
  status: WorkOrderComplementStatus;
}

export interface ListWorkOrdersQuery extends Omit<PageRequest, 'order'> {
  search?: string;
  companiesIds?: number[];
  company?: string;
  number?: string;
  estimator?: string;
  customer?: string;
  customerType?: CustomerType;
  insurer?: string;
  licensePlate?: string;
  vehicle?: string;
  entryDate?: string;
  entryInitialDate?: string;
  entryFinalDate?: string;
  authorizationDate?: string;
  authorizationInitialDate?: string;
  authorizationFinalDate?: string;
  closingDate?: string;
  closingInitialDate?: string;
  closingFinalDate?: string;
  estimatedLeaveDate?: string;
  estimatedLeaveInitialDate?: string;
  estimatedLeaveFinalDate?: string;
  returnDate?: string;
  status?: WorkOrderComplementStatusNumber[];
  type?: WorkOrderComplementType;
  cost?: number;
  tagsId?: number[];
  statusId?: number[];
  complementsReplicated?: boolean;
  orderColumns?: WorkOrderOrderColumnType;
  order?: 0 | 1;
  onlyFirstComplements?: boolean;
  prisma?: string;
  onlyVehicleEntriesTheMonth?: boolean;
  onlyVehicleDeparturesTheMonth?: boolean;
  fleet?: string;
  isPortoBatteryWorkOrder?: boolean;
  workOrderWithBatteryWarranty?: boolean;
  indicatedBy?: string;
  complementOrigin?: number[];
  scheduleStartDate?: string;
  scheduleEndDate?: string;
}

export enum ComplementOriginEnum {
  Default = 'domain.enums.complement_origin.default',
  Cilia = 'domain.enums.complement_origin.cilia',
  Soma = 'domain.enums.complement_origin.soma',
  I360 = 'domain.enums.complement_origin.i360',
  Audatexweb = 'domain.enums.complement_origin.audatexweb',
  JsonImport = 'domain.enums.complement_origin.json_import',
  VisualManagement = 'domain.enums.complement_origin.visual_management',
  Diagnostic = 'domain.enums.complement_origin.diagnostic',
  Checklist = 'domain.enums.complement_origin.checklist',
  ChecklistExpress = 'domain.enums.complement_origin.checklist_express',
  PortoBattery = 'domain.enums.complement_origin.porto_battery',
  MyBoschCarService = 'domain.enums.complement_origin.my_bosch_car_service',
  PortoSchedule = 'domain.enums.complement_origin.porto_schedule',
  PortoScheduleInternal = 'domain.enums.complement_origin.internal_porto_schedule',
  Replicated = 'domain.enums.complement_origin.replicated',
}

export const ComplementOriginTranslator: Record<ComplementOriginEnum, string> =
  {
    [ComplementOriginEnum.Default]: 'Interno',
    [ComplementOriginEnum.Cilia]: 'Cilia',
    [ComplementOriginEnum.Soma]: 'Soma',
    [ComplementOriginEnum.I360]: 'i360',
    [ComplementOriginEnum.Audatexweb]: 'Audatexweb',
    [ComplementOriginEnum.JsonImport]: 'Importação JSON',
    [ComplementOriginEnum.VisualManagement]: 'Gestão de pátio',
    [ComplementOriginEnum.Diagnostic]: 'Diagnóstico',
    [ComplementOriginEnum.Checklist]: 'Checklist',
    [ComplementOriginEnum.ChecklistExpress]: 'Checklist express',
    [ComplementOriginEnum.PortoBattery]: 'Bateria 7051',
    [ComplementOriginEnum.MyBoschCarService]: 'Meu Bosch Car Service',
    [ComplementOriginEnum.PortoSchedule]: 'Agendamento Porto',
    [ComplementOriginEnum.PortoScheduleInternal]: 'Agendamento Porto - Interno',
    [ComplementOriginEnum.Replicated]: 'Replicado',
  };

export enum WorkOrderOrderColumnType {
  tag = 'domain.enums.order_columns_work_order.tag',
  situation = 'domain.enums.order_columns_work_order.situation',
  work_order_number = 'domain.enums.order_columns_work_order.work_order_number',
  company = 'domain.enums.order_columns_work_order.company',
  insurer = 'domain.enums.order_columns_work_order.insurer',
  estimator = 'domain.enums.order_columns_work_order.estimator',
  customer = 'domain.enums.order_columns_work_order.customer',
  license_plate = 'domain.enums.order_columns_work_order.license_plate',
  vehicle = 'domain.enums.order_columns_work_order.vehicle',
  entry_date = 'domain.enums.order_columns_work_order.entry_date',
  authorization_date = 'domain.enums.order_columns_work_order.authorization_date',
  closing_date = 'domain.enums.order_columns_work_order.closing_date',
  return_date = 'domain.enums.order_columns_work_order.return_date',
  estimated_leave_date = 'domain.enums.order_columns_work_order.estimated_leave_date',
  status = 'domain.enums.order_columns_work_order.status',
  price = 'domain.enums.order_columns_work_order.price',
  type = 'domain.enums.order_columns_work_order.type',
  chassi = 'domain.enums.order_columns_work_order.chassi',
  motorization = 'domain.enums.order_columns_work_order.motorization',
  schedule_date = 'domain.enums.order_columns_work_order.schedule_date',
}

export const WorkorderColumnOrderConfig: Record<
  string,
  WorkOrderOrderColumnType
> = {
  [WorkorderColumn.Tag]: WorkOrderOrderColumnType.tag,
  [WorkorderColumn.activityStatus]: WorkOrderOrderColumnType.situation,
  [WorkorderColumn.Number]: WorkOrderOrderColumnType.work_order_number,
  [WorkorderColumn.Company]: WorkOrderOrderColumnType.company,
  [WorkorderColumn.Estimator]: WorkOrderOrderColumnType.estimator,
  [WorkorderColumn.Customer]: WorkOrderOrderColumnType.customer,
  [WorkorderColumn.Insurer]: WorkOrderOrderColumnType.insurer,
  [WorkorderColumn.Plate]: WorkOrderOrderColumnType.license_plate,
  [WorkorderColumn.Vehicle]: WorkOrderOrderColumnType.vehicle,
  [WorkorderColumn.EntryDate]: WorkOrderOrderColumnType.entry_date,
  [WorkorderColumn.EstimatedLeaveDate]:
    WorkOrderOrderColumnType.estimated_leave_date,
  [WorkorderColumn.AuthorizationDate]:
    WorkOrderOrderColumnType.authorization_date,
  [WorkorderColumn.CloseDate]: WorkOrderOrderColumnType.closing_date,
  [WorkorderColumn.ReturnDate]: WorkOrderOrderColumnType.return_date,
  [WorkorderColumn.Status]: WorkOrderOrderColumnType.status,
  [WorkorderColumn.Cost]: WorkOrderOrderColumnType.price,
  [WorkorderColumn.Type]: WorkOrderOrderColumnType.type,
  [WorkorderColumn.Chassi]: WorkOrderOrderColumnType.chassi,
  [WorkorderColumn.Motorization]: WorkOrderOrderColumnType.motorization,
  [WorkorderColumn.PortoScheduleDate]: WorkOrderOrderColumnType.schedule_date,
};

export interface ListWorkOrdersResult {
  workOrderId: number;
  complementNumber: number;
  complementId: number;
  complementIdentifier: string;
  complementAuthorizedSignature: string;
  company: {
    id: number;
    name: string;
  };
  estimator: {
    id: number;
    name: string;
  };
  customer: {
    id: number;
    name: string;
    creditLimit?: number;
  };
  insurer: {
    id: number;
    name: string;
  };
  tag?: {
    id: number;
    name: string;
    color: string;
  };
  activityStatus?: {
    id: number;
    name: string;
    color: string;
  };
  vehicle: string;
  vehicleId?: number;
  licensePlate: string;
  entryDate: string;
  authorizationDate: string;
  closingDate: string;
  returnDate: string;
  status: WorkOrderComplementStatus;
  type: WorkOrderComplementType;
  hasCommission: boolean;
  price: number;
  prisma?: string;
  isPortoBatteryWorkOrder?: boolean;
  chassi?: string;
  motorization?: string;
  complementOrigin?: ComplementOriginEnum;
  pecaAiBasketId?: string;
}

export interface CreateWorkOrderRequest {
  companyId?: number;
  estimatorId?: number;
  customerType?: CustomerType;
  workOrderType?: WorkOrderComplementType;
  insurerId?: number;
  accidentNumber?: string;
  insurerDeductibleAmount?: number;
  entryDate?: string;
  estimatedLeaveDate?: string;
  customerId?: number;
  vehicleId?: number;
  prisma?: string;
  currentKilometers?: number;
  tankLevel?: WorkOrderComplementTankLevel;
  lastOilChange?: string;
  lastOilChangeKilometers?: number;
  activityId?: string;
  applyPartnerDiscount?: boolean;
  applyEmployeeDiscount?: boolean;
  internalSchedule?: boolean;
  capsCode?: string;
  scheduleDuration?: number;
  scheduledDate?: string;
}

export interface UpdateWorkOrderRequest {
  companyId?: number;
  customerId?: number;
  vehicleId?: number;
}

export interface WorkOrderCalendarQueryParams {
  dateType?: WorkOrderCalendarDateType;
  startDate: string;
  endDate: string;
}

export type WorkorderItemCalendar = ListWorkOrdersResult & {
  price: number;
  partsPrice: number;
  servicesPrice: number;
  estimatedLeaveDate?: string;
};

export interface ComplementReplicateItemsRequest {
  itemId: number;
  type: WorkOrderItemType;
  manufacturingCode: string;
  itemDescription: string;
  supplierId: number;
  quantity: number;
  unitPrice: number;
  unitCost: number;
  discount: number;
  isInternal: boolean;
  subAreaId: number;
  manufacturerId: number;
  isPaintedPart: boolean;
  paint: number;
  change: number;
  repair: number;
}

export interface ComplementReplicateRequest {
  workOrderId: number;
  workOrderComplementId: number;
  status: WorkOrderComplementStatus;
  estimatedLeaveDate?: string;
  returnDate?: string;
  authorizationDate?: string;
  prisma?: string;
  currentKilometers?: number;
  tankLevel?: WorkOrderComplementTankLevel;
  items?: ComplementReplicateItemsRequest[];
}

export interface ComplementReplicateResponse {
  workOrderId: number;
  workOrderComplementId: number;
}

export enum WorkOrderCreateByPolicyCompanyCode {
  porto = 'domain.enums.company_code.porto',
  itau = 'domain.enums.company_code.itau',
  azul = 'domain.enums.company_code.azul',
  mitsui = 'domain.enums.company_code.mitsui',
}

export interface WorkOrderCreateByPorto {
  companyId: number;
  activityId?: string;
  companyCode?: WorkOrderCreateByPolicyCompanyCode;
  estimatorId: number;
  customerType: string;
  workOrderType: string;
  insurerId?: number;
  accidentNumber?: string;
  insurerDeductibleAmount?: number;
  entryDate?: string;
  policyNumber?: string;
  vehicleId?: number;
  customerId?: number;
  customer?: {
    name?: string;
    birthDate?: string;
    genderType?: string;
    documentNumber?: string;
    contacts?: { email: string; phone: string };
    address?: {
      cep: string;
      state: string;
      city: string;
      district: string;
      street: string;
      number: string;
      complement?: string;
    };
  };
  vehicle?: {
    chassi?: string;
    licensePlate?: string;
    prisma?: string;
    tankLevel?: WorkOrderComplementTankLevel;
    currentKilometers?: number;
    model?: string;
    brand?: string;
    yearFabrication?: number;
    yearModel?: number;
  };
  lastOilChange?: string;
  lastOilChangeKilometers?: number;
  diagnosticId?: number;
  includeParts?: boolean;
  chargerDiagnostic?: boolean;
  partIds?: number[];
  hasPortoVoucher?: boolean;
  internalSchedule?: boolean;
  capsCode?: string;
  scheduleDuration?: number;
  scheduledDate?: string;
}

export interface WorkOrderMigrateConsultListRequest {
  companyId?: number;
  entryDate?: string;
  authorizationDate?: string;
  closingDate?: string;
  estimatedLeaveDate?: string;
  returnDate?: string;
  status?: WorkOrderComplementStatus;
  type?: WorkOrderComplementType;
  search?: string;
  nextToken?: number;
  rows?: number;
  order?: 0 | 1;
  originSystemIds?: number[];
  hasLinkedWorkOrders?: boolean;
}

export interface WorkOrderMigrateConsult {
  id: number;
  companyName: string;
  customer: string;
  licensePlate?: string;
  workOrderNumber: number;
  workOrderComplementNumber: number;
  estimator?: string;
  status: WorkOrderComplementStatus;
  type: WorkOrderComplementType;
  insurer?: string;
  accidentNumber?: string;
  insurerDeductibleAmount?: number;
  entryDate?: string;
  authorizationDate?: string;
  closingDate?: string;
  estimatedLeaveDate?: string;
  returnDate?: string;
  prisma?: string;
  currentKilometers?: number;
  cancellationReason?: string;
  complementIdentifier: string;
  policyNumber?: string;
  model?: string;
  brand?: string;
  observation?: string;
  origin?: string;
  boundWorkOrder?: { complementId: number; workOrderId: number };
}

export type WorkOrderMigrateConsultListResponse =
  PageResult<WorkOrderMigrateConsult>;

export interface WorkOrderMigrateConsultItem {
  id: number;
  workOrderConsultId: number;
  isPart: boolean;
  itemDescription: string;
  supplier: string | null;
  manufacturer: string | null;
  type: WorkOrderItemType;
  quantity: number;
  unitPrice: number;
  discount: number;
  unitCost: number;
  isInternal: boolean;
  subArea: string | null;
  manufacturingCode: string | null;
  employee?: string;
  isItemRejectedByCustomer: boolean;
}

export interface WorkOrderMigrateConsultItemResponse {
  partTotal: number;
  serviceTotal: number;
  total: number;
  items: WorkOrderMigrateConsultItem[];
}

export interface WorkOrderCustomerAddressRequest {
  workOrderId: number;
  customerAddressId?: number;
}

export interface WorkOrderPartnerDiscountRequest {
  applyPartnerDiscount: boolean;
  workOrderId: number;
}

export interface WorkOrderComplementBenefitListRequest {
  workOrderId?: number;
  showOnlyUsedBenefits?: boolean;
}

export interface WorkOrderComplementBenefitList {
  complementId: number;
  complementIdentifier: string;
  items: WorkOrderComplementBenefit[];
}

export interface WorkOrderComplementBenefit {
  id: number;
  description: string;
  quantity: 1;
  imageUrl: string | null;
  benefitName: string;
  benefitServiceCode: string;
  benefitMaterialId: string | null;
  benefitId: number;
  isCourtesy: boolean;
}

export interface WorkOrderEmployeerDiscountRequest {
  applyEmployeeDiscount: boolean;
  workOrderId: number;
}

export enum WorkOrderAddPolicyCompanyCodeEnum {
  PORTO = 'domain.enums.companyCode.porto',
  ITAU = 'domain.enums.companyCode.itau',
  AZUL = 'domain.enums.companyCode.azul',
  MITSUI = 'domain.enums.companyCode.mitsui',
}

export const WorkOrderAddPolicyCompanyCode: Record<
  string,
  WorkOrderAddPolicyCompanyCodeEnum
> = {
  PORTO: WorkOrderAddPolicyCompanyCodeEnum.PORTO,
  ITAU: WorkOrderAddPolicyCompanyCodeEnum.ITAU,
  AZUL: WorkOrderAddPolicyCompanyCodeEnum.AZUL,
  MITSUI: WorkOrderAddPolicyCompanyCodeEnum.MITSUI,
};
export interface WorkOrderAddPolicyRequest {
  complementId: number;
  policyNumber: string;
  vehicleId: number;
  lastOilChange: string;
  lastOilChangeKilometers: number;
  companyCode: WorkOrderAddPolicyCompanyCodeEnum;
  agreementCode?: string;
  vehicle: {
    chassi: string;
    licensePlate: string;
    prisma?: string;
    tankLevel?: WorkOrderComplementTankLevel;
    currentKilometers: number;
    brand: string;
    model: string;
    yearFabrication: number;
    yearModel: number;
  };
}
export interface WorkOrderSystemOrigin {
  id: number;
  name: string;
}

export interface WorkOrderSystemOriginListResponse {
  systems: WorkOrderSystemOrigin[];
}

export interface WorkOrderMigrationConsultBindComplement {
  migratedComplementId: number;
  complementId: number;
  workOrderId: number;
}
